// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState } from 'vuex';
import loading from '../../../../share/components/vue/ProcessingIcon';
import logWrapper from '../../../../share/logWrapper';
import util from '../../../../share/util/utils';
import inputValidator from '../../../../share/util/inputValidator';
import eventLogUtil from '../../../../share/util/eventLogUtil';
import cstmProgress from '../../../../share/components/vue/CustomProgress';

export default {

  data() {
    return {
      modelUpdateProcessing: true,
      turnIgOnChecked: false,
      processing: false,
      isCancelLocked: false,
      otpw: '',
      isAlert: {
        data: { valid: null, msg: '' }
      },
      startTime: null,
      elapsedSeconds: 0,
      timerInterval: null,

      // 各種ダイアログのバインド値
      deleteallkeysconfirmotpwinput: false,
      deleteallkeyserrorconfirmotpw: false,
      deleteallkeysconfirmtostart: false,
      deleteallkeyssuccess: false,
      deleteallkeysconfirmigon: false,
      deleteallkeysconfirmigison: false,
      deleteallkeyswarningigon: false,
      deleteallkeysprogresscommimbcm: false,
      deleteallkeysprocessing: false,
      deleteallkeyswarningkeynotfound: false,
      deleteallkeyswarningcancel: false,
      deleteallkeysfailure: false,
      deleteallkeysend: false,
      nextModalId: null
    };
  },

  props: {
    onDeleteAllKeysOkButtonClick: Function,
    onDeleteAllKeysOkButtonPushedOtpwVerifyAPICall: Function,
    backEventOnCancleButtonClick: Function,
    igOnOkButtonPushedDeleteAllKeyIgOnAPICall: Function,
    onDeleteAllKeyCancelAPICall: Function,
    onErrorOkButtonClick: Function,
    showUnexpectedErrorModal: Function,
    notifyEndOfProcess: Function,
    imbStatusInfo: Object,
    onSetEndOfProcessingFlag: Function,
    isCausesAndActionDialog: Function,
    changeProcessingStatus: Function,
    modalShowInfo: {
      type: Object,
      required: false,
      default: () => ({ id: "", isShow: false }),
    },
  },

  components: {
    'processing-view': loading,
    'cstm-progress': cstmProgress,
  },

  computed: {
    ...mapState({
      vehicle: state => state.vehicle,
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleType: state => state.immobilizer.vehicleType,
    }),
    isType2() {
      return ['G', 'I'].includes(this.vehicleType);
    },
    formattedTime() {
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    progressValue() {
      const MAX_DURATION = 900;
      if (this.elapsedSeconds >= MAX_DURATION) {
        return 100;
      }
      return Math.min((this.elapsedSeconds / MAX_DURATION) * 100, 100);
    },
    progressVariant() {
      return 'primary';
    },
  },

  watch: {
    processing: function (value) {
      this.changeProcessingStatus(value);
    },

    modalShowInfo: {
      handler(value) {
        if (value) {
          const id = value.id.replace(/-/g, "");
          if (util.hasPropertyCheck(this, id)) {
            this[id] = value.isShow;
          }
        }
      },
      deep: true,
    },
  },

  methods: {

    /** OTPWクリアイベント */
    clearInputAlert() {
      // OTPWをクリア
      this.isAlert = {
        data: { valid: null, msg: '' }
      };
    },

    /** 
     * バリデーションチェック 
     * @returns {boolean} 判定結果
     */
    validate() {
      this.isAlert.data = inputValidator.validateRequirAndLength(
        this.otpw, true, 9, /^[A-Z0-9!\-#$%&=~?/_]*$/
      );
      return this.isAlert.data.valid === null;
    },

    /** OTPW入力し、OKボタン押下処理 */
    onOtpwInputOkButtonClick: async function () {
      // OTPWをクリア
      this.clearInputAlert();
      // 入力チェック
      if (!this.validate()) {
        return;
      }
      // OTPW照合処理APIを呼び出す
      try {
        await this.onDeleteAllKeysOkButtonPushedOtpwVerifyAPICall(this.otpw);
      } finally {
        this.otpw = '';
      }
    },

    /** 全キー消去確認ダイアログでOKボタン押下時処理 */
    onDeleteAllKeysConfirmToStartOkButtonClick: function () {
      this.dialogCloseProcess();
      this.onDeleteAllKeysOkButtonClick();
    },

    /** 全キー消去正常終了ダイアログでOKボタン押下時処理 */
    successOkButtonClick: function () {
      this.dialogCloseProcess();
      this.nextModalId = 'delete-all-keys-end';
    },

    /** 全キー消去終了確認ダイアログでOKボタン押下時処理 */
    onDeleteAllKeysEndOkButtonClick: function () {
      this.dialogCloseProcess();
      this.isCancelLocked = false;
      this.turnIgOnChecked = false;
      this.determineEndOfProcessingFlag();
    },

    /** IGN-ON指示ダイアログでキャンセル/IGN-ON確認エラーダイアログでOK押下時の共通終了処理 */
      onIgOnDialogEndOfProcessButtonClick: async function () {
      try {
        this.processing = true;
        await this.backEventOnCancleButtonClick();
        eventLogUtil.updateImbEventLogCanceled(this.netAppId);
      } catch (error) {
        // 'Unexpected error has occurred'
        this.showUnexpectedErrorModal(this.$t('imb.error_imb_system_cause'));
      } finally {
        this.processing = false;
        this.turnIgOnChecked = false;
      }
      this.determineEndOfProcessingFlag();
    },

    /** IGN-ON指示ダイアログまたは再IGN-ON指示ダイアログでOKボタン押下時処理 */
    igOnOkButtonClick: async function () {
      this.dialogCloseProcess();
      await this.igOnOkButtonPushedDeleteAllKeyIgOnAPICall();
      this.turnIgOnChecked = false;
    },

    /** 全キー消去実行中ダイアログでcancelボタン押下時処理 */
    deleteAllKeysCancelButtonClick: async function () {
      try {
        this.isCancelLocked = true;
        this.processing = true;
        await this.onDeleteAllKeyCancelAPICall();
      } finally {
        this.processing = false;
      }
    },

    /** 経過時間タイマーの開始処理 */
    startElapsedTimeTimer() {
      if (this.timerInterval) {
        return;
      }
      this.startTime = Date.now();
      this.elapsedSeconds = 0;
      this.timerInterval = setInterval(() => {
        const now = Date.now();
        this.elapsedSeconds = Math.floor((now - this.startTime) / 1000);
      }, 1000);
    },

    /** 全キー消去実行中ダイアログの @show ハンドラ */
    onProcessingModalShow() {
      this.modalShowWEvent('show');
      this.startElapsedTimeTimer();
    },

    /** 経過時間タイマーの停止処理 */
    stopElapsedTimeTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      this.elapsedSeconds = 0;
      this.startTime = null;
    },

    /** 全キー消去実行中ダイアログの @hidden ハンドラ */
    onProcessingModalHidden() {
      this.modalShowWEvent('hide');
      this.stopElapsedTimeTimer();
    },

    /** OTPW入力ダイアログまたは全キー消去確認ダイアログでcancelボタン押下時処理 */
    cancelButtonClick: function () {
      this.otpw = '';
      this.dialogCloseProcess();
    },

    /** キー未登録エラーダイアログでOKボタン押下時処理 */
    onWarningKeyNotFoundOkButtonClick: async function () {
      this.processing = true;
      this.dialogCloseProcess();
      await this.notifyEndOfProcess();
      this.processing = false;
    },

    /** 車両情報が null の場合（途中復帰時）、親コンポーネントへ処理完了を通知し、Home画面へ遷移する。 */
    determineEndOfProcessingFlag: function () {
      if (this.vehicle.vehicleInfo === null) {
        logWrapper.log('vehicle information is ', this.vehicle.vehicleInfo);
        this.onSetEndOfProcessingFlag();
      }
    },

    /**
     * ダイアログの表示状態と次のダイアログ情報を更新し、親コンポーネントへ通知する
     * @param {string} mode ダイアログの表示状態
     */
    modalShowWEvent: function (mode) {
      const info = { id: this.modalShowInfo.id, isShow: false, nextModal: this.modalShowInfo.nextModal };
      if (mode === "show") {
        info.isShow = true;
      } else {
        // 次のダイアログが設定されていた場合のみ
        if (this.nextModalId) {
          info.nextModal = { id: this.nextModalId, msg: "" };
          this.nextModalId = null;
        }
      }
      this.$emit("modalDisplayStatus", info);
    },

    /** ダイアログの閉じる処理 */
    dialogCloseProcess: function () {
      const id = this.modalShowInfo.id.replace(/-/g, "");
      if (util.hasPropertyCheck(this, id)) {
        this[id] = false;
      }
    }

  },

  created() { },

  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  },

};
